// View as customer (ENG-2337). Browser storage and in-memory caches belong to
// whoever was signed in when they were filled, so a change of identity (start,
// exit, or end of a view-as session) clears them and reloads every tab.

export const IDENTITY_KEY = "ordinal:identity";

/** The `view_as` block of /api/v1/logged_in, present only during a session. */
export interface ViewAs {
  session_id: string;
  customer_email: string;
  expires_at: string;
}

export function identityFor(userId: number, viewAs: ViewAs | null | undefined): string {
  return `${userId}:${viewAs?.session_id ?? ""}`;
}

export function clearOrdinalStorage(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("ordinal")) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    // Storage unavailable (private mode); there is nothing stale to clear.
  }
}

/**
 * The server ended the view-as session (409 view_as_ended) and already serves
 * this browser as the employee. Leave the customer's view before anything else
 * runs on it, for the location the server names.
 */
export function leaveEndedViewAs(location: unknown): void {
  clearOrdinalStorage();
  window.location.assign(typeof location === "string" && location !== "" ? location : "/");
}

/**
 * Records the identity this tab is running as. Returns true when it differs
 * from the one stored by an earlier page, meaning storage was cleared and the
 * page must reload before anything reads the old identity's state. A first
 * visit with no stored identity only clears when a view-as session is live.
 */
export function syncIdentity(identity: string, viewing: boolean): boolean {
  let stored: string | null;
  try {
    stored = localStorage.getItem(IDENTITY_KEY);
  } catch {
    return false;
  }
  if (stored === identity) return false;

  const changed = stored !== null || viewing;
  if (changed) clearOrdinalStorage();
  try {
    localStorage.setItem(IDENTITY_KEY, identity);
  } catch {
    // Without a stored identity the next load cannot compare, so a reload
    // here would repeat forever. Storage is already cleared; carry on.
    return false;
  }
  return changed;
}
